import React, { useEffect, useState } from 'react'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import TextLink from '@toasttab/shared/src/components/molecules/TextLink/TextLink'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import {
  getIsActiveDate,
  getIsActivePage
} from '@toasttab/shared/src/utils/bannerUtils'
import { GlobalBannerData, BannerTheme } from './GlobalBanner.d'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIconLight } from '@toasttab/shared/src/assets/svg/icon-close-light.svg'
// eslint-disable-next-line no-restricted-imports
import { ReactComponent as CloseIconDark } from '@toasttab/shared/src/assets/svg/icon-close.svg'

const themeStyles: Record<BannerTheme, { background: string; text: string }> = {
  green: {
    background: 'bg-grass-25',
    text: 'text-black'
  },
  yellow: {
    background: 'bg-yellow-25',
    text: 'text-black'
  },
  charcoal: {
    background: 'bg-gray-125',
    text: 'text-white'
  },
  blue: {
    background: 'bg-blue-0',
    text: 'text-black'
  },
  default: {
    background: 'bg-blue-0',
    text: 'text-black'
  }
}

const GlobalBanner = () => {
  const [activeBanner, setActiveBanner] = useState<GlobalBannerData | null>(
    null
  )
  const { globalSettings, activeGlobalBanner, region, setActiveGlobalBanner } =
    usePageContext()

  useEffect(() => {
    // Filter to active entries only for the correct region
    const activeBannerData = globalSettings?.globalBanner.filter(
      ({ activePages, country, endDate, startDate }: GlobalBannerData) =>
        country === region &&
        getIsActiveDate(startDate, endDate) &&
        getIsActivePage(activePages)
    )
    // If we have an active banner, save the first one in state
    if (
      activeBannerData?.length > 0 &&
      sessionStorage.getItem(activeBannerData[0].contentful_id) !== 'true'
    ) {
      setActiveBanner(activeBannerData[0])
    } else {
      setActiveBanner(null)
    }
  }, [globalSettings?.globalBanner, region, setActiveBanner])

  useEffect(() => {
    if (activeBanner && activeBanner.contentful_id !== activeGlobalBanner) {
      // Save active banner ID to global context
      setActiveGlobalBanner(activeBanner.contentful_id)
    }
  }, [activeBanner, activeGlobalBanner, setActiveGlobalBanner])

  const handleClose = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): void => {
    event.preventDefault()
    setActiveBanner(null)
    setActiveGlobalBanner(null)
    sessionStorage.setItem(activeBanner.contentful_id, 'true')
  }
  const currentTheme = themeStyles[activeBanner?.theme || 'default']
  return (
    <>
      {activeBanner ? (
        <div className='py-2 overflow-hidden' data-testid='GlobalBanner'>
          <div className='ds-wrapper'>
            <div className='ds-container'>
              <div
                className={`relative ${currentTheme.background} md:h-10 min-h-[40px] text-base rounded-lg`}
              >
                <div className='py-2 md:flex md:justify-center md:items-center md:h-full'>
                  <a
                    className='flex justify-center items-center h-full text-center md:items-start'
                    data-testid='GlobalBanner-link'
                    href={activeBanner.cta?.url}
                  >
                    <div className='w-[300px] sm:w-[450px] md:w-full md:inline-flex'>
                      <strong
                        className={`font-sansPro font-semibold ${currentTheme.text} tracking-[.2px]`}
                      >
                        {renderRichText(activeBanner.heading)}
                      </strong>
                      {activeBanner.subheading?.raw ? (
                        <TextLink
                          classes='ml-2 md:ml-8 leading-none'
                          el='span'
                          icon='arrowRight'
                          color={
                            (activeBanner?.theme || 'default') === 'charcoal'
                              ? 'light'
                              : 'default'
                          }
                          text={
                            renderRichText(activeBanner.subheading) as string
                          }
                        />
                      ) : null}
                    </div>
                  </a>
                </div>
                <button
                  className='absolute top-1 right-1'
                  onClick={handleClose}
                >
                  {(activeBanner?.theme || 'default') === 'charcoal' ? (
                    <CloseIconLight height='30' width='30' />
                  ) : (
                    <CloseIconDark height='30' width='30' />
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  )
}

export default GlobalBanner
